import * as React from "react";
import * as Toast from "@radix-ui/react-toast";
import { XIcon } from '@phosphor-icons/react';
import { CSSProperties, useEffect, useState, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from '@tranquility-ai/theme-components/button';
import { StatsContext } from '@/app/contexts';
import { TOAST_Z_CLASS, TOAST_Z_INDEX, unwatchToastLayer, watchToastLayer } from './toast-layer';

export {
  TOAST_LAYER_ID,
  TOAST_Z_CLASS,
  TOAST_Z_INDEX,
  ensureToastLayer,
  unwatchToastLayer,
  watchToastLayer,
} from './toast-layer';

export enum ToastLevel {
  INFO = 'info',
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning'
}

export const ToastProvider = Toast.Provider;

/** Fixed viewport for Radix toast portal; does not affect document layout or scroll. */
export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof Toast.Viewport>,
  React.ComponentPropsWithoutRef<typeof Toast.Viewport>
>(function ToastViewport({ className, style, ...props }, ref) {
  return (
    <Toast.Viewport
      ref={ref}
      className={[
        'fixed bottom-0 right-0',
        TOAST_Z_CLASS,
        'flex flex-col gap-2 p-4 pr-20',
        'w-[min(390px,calc(100vw-2rem))] m-0 list-none outline-none',
        'pointer-events-none [&>*]:pointer-events-auto',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ zIndex: TOAST_Z_INDEX, ...style }}
      {...props}
    />
  );
});
ToastViewport.displayName = 'ToastViewport';

const toastColors = (level: ToastLevel) => {
  switch(level) {
    case ToastLevel.ERROR:
      return {
        titleBg: 'bg-red-700',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-red-700',
        actionText: 'text-white',
      };
    case ToastLevel.SUCCESS:
      return {
        titleBg: 'bg-green-700',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-green-700',
        actionText: 'text-white',
      };
    case ToastLevel.WARNING:
      return {
        titleBg: 'bg-yellow-500',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-yellow-500',
        actionText: 'text-white',
      };
    case ToastLevel.INFO:
    default:
      return {
        titleBg: 'bg-blue-500',
        titleText: 'text-white',
        descriptionBg: 'bg-white',
        descriptionText: 'text-black',
        actionBg: 'bg-blue-500',
        actionText: 'text-white',
      };
  }
};

const CLOSE_ANIMATION_MS = 500;

const TOAST_WIDTH_PX = 380;
const TOAST_INSET_PX = 16;
/** Bottom/top-right toasts sit left of the Intercom launcher (~60px + margin). */
const TOAST_RIGHT_INSET_PX = 80;

const getToastLocation = (newLocation: string, shouldClose: boolean): CSSProperties => {
  const toastWidth = TOAST_WIDTH_PX;
  const inset = TOAST_INSET_PX;
  const crustOnly = 10;
  const toastHorizontalMoveToClose = toastWidth;
  const toastVerticalMoveToClose = 180;
  const centerHorizontalPx = `calc(50% - ${toastWidth / 2}px)`;

  const base: CSSProperties = {
    position: 'fixed',
    width: `${toastWidth}px`,
    maxWidth: `calc(100vw - ${inset * 2}px)`,
    zIndex: TOAST_Z_INDEX,
  };

  if (newLocation === 'top' || newLocation === 'top-left' || newLocation === 'top-right') {
    if (newLocation === 'top-left') {
      return shouldClose
        ? { ...base, top: '90px', left: `-${toastHorizontalMoveToClose}px` }
        : { ...base, top: '90px', left: `${inset}px` };
    }
    if (newLocation === 'top-right') {
      return shouldClose
        ? { ...base, maxWidth: `calc(100vw - ${TOAST_INSET_PX}px - ${TOAST_RIGHT_INSET_PX}px)`, top: '90px', right: `-${toastHorizontalMoveToClose}px` }
        : { ...base, maxWidth: `calc(100vw - ${TOAST_INSET_PX}px - ${TOAST_RIGHT_INSET_PX}px)`, top: '90px', right: `${TOAST_RIGHT_INSET_PX}px` };
    }
    return shouldClose
      ? { ...base, top: `-${toastVerticalMoveToClose - crustOnly}px`, left: centerHorizontalPx }
      : { ...base, top: '90px', left: centerHorizontalPx };
  }

  if (newLocation === 'bottom' || newLocation === 'bottom-left' || newLocation === 'bottom-right') {
    if (newLocation === 'bottom-left') {
      return shouldClose
        ? { ...base, bottom: `${inset}px`, left: `-${toastHorizontalMoveToClose}px` }
        : { ...base, bottom: `${inset}px`, left: `${inset}px` };
    }
    if (newLocation === 'bottom-right') {
      return shouldClose
        ? { ...base, maxWidth: `calc(100vw - ${TOAST_INSET_PX}px - ${TOAST_RIGHT_INSET_PX}px)`, bottom: `${inset}px`, right: `-${toastHorizontalMoveToClose}px` }
        : { ...base, maxWidth: `calc(100vw - ${TOAST_INSET_PX}px - ${TOAST_RIGHT_INSET_PX}px)`, bottom: `${inset}px`, right: `${TOAST_RIGHT_INSET_PX}px` };
    }
    return shouldClose
      ? { ...base, bottom: `-${toastVerticalMoveToClose}px`, left: centerHorizontalPx }
      : { ...base, bottom: `${inset}px`, left: centerHorizontalPx };
  }

  return base;
};

export interface TpToastProps {
  setOpen: (open: boolean) => void;
  open?: boolean;
  title?: string;
  description?: string;
  descriptionClassName?: string;
  child?: React.ReactNode;
  actionButtonText?: string;
  actionButtonOnClick?: () => void;
  level: ToastLevel;
  duration: number;
  location: 'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right';
  time: number;
};

const TpToast = ({
  setOpen,
  open,
  title,
  description,
  descriptionClassName = 'text-md',
  child,
  actionButtonText,
  actionButtonOnClick,
  level,
  duration = 3600000, // 1 hour
  location,
  time,
}: TpToastProps) => {
  const [locationSpecs, setLocationSpecs] = useState<CSSProperties>();

  const timer = useRef<NodeJS.Timeout | null>(null);
  const timeRef = useRef<number | null>(null);
  const isDismissingRef = useRef(false);

  const dismissToast = () => {
    if (isDismissingRef.current) return;
    isDismissingRef.current = true;
    setLocationSpecs(getToastLocation(location, true));
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      setOpen(false);
      isDismissingRef.current = false;
    }, CLOSE_ANIMATION_MS);
  };

  useEffect(() => {
    if (open) {
      isDismissingRef.current = false;
      timeRef.current = time;
      setLocationSpecs(getToastLocation(location, false));
      if (timer.current) {
        clearTimeout(timer.current);
      }
      if (duration && !Number.isNaN(duration) && duration > 0) {
        timer.current = setTimeout(() => {
          if (timeRef.current === time) {
            dismissToast();
          }
        }, duration);
      }
    }
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [duration, location, open, time]);

  const setClose = () => {
    dismissToast();
  };

  const toastClass = `box-border list-none rounded-md overflow-hidden duration-500 shadow-lg flex flex-col pointer-events-auto ${toastColors(level).descriptionBg}`;
  const statsContext = useContext(StatsContext);
  return (
    <>
      <Toast.Root
        className={toastClass}
        style={locationSpecs}
        open={open}
        duration={!Number.isNaN(duration) ? duration : 3000}
        onOpenChange={(isOpen) => {
          if (!isOpen) dismissToast();
        }}
      >
        <Toast.Title className={`${toastColors(level).titleBg} rounded-t-md px-3 py-2 font-semibold leading-tight`}>
          <div className="flex flex-row justify-between items-center gap-2">
            <div className={`${toastColors(level).titleText}`}>{title}</div>
            <XIcon size={24} color={`${toastColors(level).titleText}`} onClick={setClose} className="cursor-pointer m-0 p-0 fill-white shrink-0"/>
          </div>
        </Toast.Title>
        <Toast.Description className={`${toastColors(level).descriptionBg} ${descriptionClassName} p-2`}>
          <div className={`${toastColors(level).descriptionBg} ${toastColors(level).descriptionText}`}>{description}</div>
        </Toast.Description>
        {child ? (
          <div className={`${toastColors(level).descriptionBg} px-2 pb-2`}>{child}</div>
        ) : null}
        {actionButtonText && actionButtonOnClick && (
          <div className={`${toastColors(level).descriptionBg} px-2 pb-3 pt-1`}>
            <Button
              id="ToastActionButton"
              type="button"
              size="sm"
              style="solid"
              color="accent"
              background="default"
              className={`w-full justify-center text-center text-sm font-medium ${toastColors(level).actionText} ${toastColors(level).actionBg}`}
              onClick={(event) => {
                actionButtonOnClick();
                setClose();
                statsContext.recordStat(
                  'ToastActionButtonClicked',
                  event,
                  {
                    userInfo: {
                      userId: statsContext?.userInfo?.userId || '',
                      sessionId: statsContext?.userInfo?.sessionId || '',
                      caseId: statsContext?.userInfo?.caseId || '',
                      query: actionButtonText || ''
                    }
                  },
                );
              }}
            >
              {actionButtonText}
            </Button>
          </div>
        )}
      </Toast.Root>
    </>
  )
}
export default TpToast;

type TpToastHostProps = {
  toast?: TpToastProps;
  setToastOpen: (open: boolean) => void;
};

/** Renders the active toast and viewport once, portaled into the notification
 *  layer so stacking is not trapped under modal overlays (`backdrop-filter`). */
export function TpToastHost({ toast, setToastOpen }: TpToastHostProps) {
  const [layer, setLayer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setLayer(watchToastLayer());
    return () => {
      unwatchToastLayer();
    };
  }, []);

  if (!layer) return null;

  return createPortal(
    <>
      {!!toast && (
        <TpToast
          setOpen={setToastOpen}
          open={toast.open}
          title={toast.title}
          description={toast.description}
          descriptionClassName={toast.descriptionClassName}
          actionButtonText={toast.actionButtonText}
          actionButtonOnClick={toast.actionButtonOnClick}
          level={toast.level}
          duration={toast.duration}
          location={toast.location}
          child={toast.child}
          time={toast.time}
        />
      )}
      <ToastViewport />
    </>,
    layer,
  );
}
